import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { EditModeProvider } from '@/contexts/EditModeContext';
import { Header } from '@/components/report/Header';
import { HeroSection } from '@/components/report/HeroSection';
import { AssessmentSummary } from '@/components/report/AssessmentSummary';
import { KeyInfoSection } from '@/components/report/KeyInfoSection';
import { RepairsSection } from '@/components/report/RepairsSection';
import { RTOVerificationSection } from '@/components/report/RTOVerificationSection';
import { OBDReportMobile } from '@/components/report/OBDReportMobile';
import { InspectionDetailsSection } from '@/components/report/InspectionDetailsSection';
import { VehicleDetailsSection } from '@/components/report/VehicleDetailsSection';
import { Footer } from '@/components/report/Footer';
import { ServiceHistorySection } from '@/components/report/ServiceHistorySection';
import { Skeleton } from '@/components/ui/skeleton';
import { inspectionReportData } from '@/data/inspectionData';
import { StandardReportStyle, PremiumReportStyle, DetailedTechnicalReportStyle } from '@/components/report/ReportStyles';
import '@/styles/inspection-report.css';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Loading skeleton component
function ReportSkeleton() {
  return (
    <div className="inspection-report min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

// Error component
function ReportError({ error, onRetry }) {
  return (
    <div className="inspection-report min-h-screen flex items-center justify-center p-4">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-red-600 mb-2">Failed to load report</h2>
        <p className="text-gray-500 mb-4">{error}</p>
        <button 
          onClick={onRetry}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

// Transform CRM inspection data to report format
function transformInspectionToReport(inspection, lead, customer) {
  // Extract AI insights if available
  const aiInsights = inspection.ai_insights || {};
  const conditionRatings = aiInsights.condition_ratings || {};
  
  // Calculate conditions from category ratings (same logic as LiveProgressModal)
  const calculateConditionFromRatings = (categoryRatings, groupKeywords) => {
    if (!categoryRatings || typeof categoryRatings !== 'object') return null;
    
    const ratings = [];
    Object.entries(categoryRatings).forEach(([key, value]) => {
      const keyLower = (key || '').toLowerCase();
      const rating = typeof value === 'object' && value !== null ? (value.rating ?? 0) : (value ?? 0);
      
      const matchesGroup = groupKeywords.some(keyword => keyLower.includes(keyword));
      if (matchesGroup && rating > 0) {
        ratings.push(rating);
      }
    });
    
    if (ratings.length === 0) return null;
    const avg = Math.round(ratings.reduce((a, b) => a + b, 0) / ratings.length);
    
    if (avg >= 8) return 'GOOD';
    if (avg >= 6) return 'FAIR';
    if (avg >= 4) return 'AVERAGE';
    if (avg > 0) return 'POOR';
    return 'PENDING';
  };
  
  const engineKeywords = ['engine', 'exhaust', 'oil', 'battery', 'electrical', 'obd', 'scanner', 'health', 'diagnosis'];
  const exteriorKeywords = ['exterior', 'bumper', 'fender', 'headlight', 'taillight', 'hood', 'trunk', 'windshield', 'glass', 'door panel', 'tyre', 'tire', 'wheel', 'physical'];
  const interiorKeywords = ['interior', 'infotainment', 'air condition', 'ac ', 'cooling', 'accessories'];
  const transmissionKeywords = ['transmission', 'gearbox', 'clutch', 'suspension', 'brake'];
  
  const calculatedConditions = {
    engine: calculateConditionFromRatings(inspection.category_ratings, engineKeywords),
    exterior: calculateConditionFromRatings(inspection.category_ratings, exteriorKeywords),
    interior: calculateConditionFromRatings(inspection.category_ratings, interiorKeywords),
    transmission: calculateConditionFromRatings(inspection.category_ratings, transmissionKeywords)
  };
  
  // Default structure matching the report format
  const reportData = {
    header: {
      reportFor: inspection.package_name || "Inspection Report",
      customerName: customer?.name || lead?.customer_name || "Customer",
      customerPhone: customer?.phone || lead?.phone_number || "",
      vehicleNumber: lead?.vehicle_number || inspection.vehicle_number || "",
      inspectionRequestedDate: lead?.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "-",
      inspectedOn: inspection.inspection_date ? new Date(inspection.inspection_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "-",
      inspectedBy: inspection.mechanic_name || "WiseDrive Mechanic",
      inspectionType: inspection.package_name || "Standard",
      location: inspection.city || lead?.city || "",
      marketValue: {
        min: aiInsights.market_value?.min || inspection.market_value_min || 0,
        max: aiInsights.market_value?.max || inspection.market_value_max || 0,
        currency: "₹",
        confidence: aiInsights.market_value?.confidence || "medium"
      },
      recommendedToBuy: aiInsights.recommended_to_buy ?? inspection.recommended_to_buy ?? false,
      overallRating: aiInsights.overall_rating || inspection.overall_rating || 0,
      checkpointsInspected: inspection.checkpoints_inspected || 0,
      isPublished: inspection.report_published || false,
      lastSaved: inspection.updated_at ? new Date(inspection.updated_at).toLocaleString('en-IN') : "-",
      aiGenerated: aiInsights.ai_generated || false,
      aiGeneratedAt: aiInsights.generated_at || inspection.ai_report_generated_at || null
    },

    vehicleInfo: {
      make: lead?.vehicle_make || inspection.vehicle_make || "",
      model: lead?.vehicle_model || inspection.vehicle_model || "",
      year: lead?.vehicle_year || inspection.vehicle_year || 0,
      mfgDate: inspection.mfg_date || "",
      fuel: lead?.fuel_type || inspection.fuel_type || "",
      transmission: inspection.transmission || "",
      owners: inspection.owners || 0,
      regNo: inspection.car_number || lead?.vehicle_number || inspection.vehicle_number || "",
      colour: inspection.vehicle_colour || "",
      regDate: inspection.reg_date || "",
      engineCC: inspection.engine_cc || 0,
      engineNo: inspection.engine_number || inspection.engine_no || "",
      chassisNo: inspection.chassis_number || inspection.chassis_no || ""
    },

    assessmentSummary: {
      // assessment_summary might be a string OR an object with an 'overall' key
      paragraph: typeof aiInsights.assessment_summary === 'string' 
        ? aiInsights.assessment_summary 
        : (aiInsights.assessment_summary?.overall || inspection.assessment_summary || "This vehicle has been inspected. Detailed assessment pending."),
      keyHighlights: aiInsights.key_highlights || inspection.key_highlights || [],
      riskFactors: aiInsights.risk_factors || [],
      recommendations: aiInsights.recommendations || []
    },

    keyInfo: {
      kmsDriven: inspection.kms_driven || lead?.kms_driven || 0,
      // Priority: DB value > Calculated from category ratings > AI insights > PENDING
      engineCondition: (inspection.engine_condition && inspection.engine_condition !== 'PENDING') 
        ? inspection.engine_condition 
        : (calculatedConditions.engine || conditionRatings.engine || "PENDING"),
      interiorCondition: (inspection.interior_condition && inspection.interior_condition !== 'PENDING') 
        ? inspection.interior_condition 
        : (calculatedConditions.interior || conditionRatings.interior || "PENDING"),
      transmission: (inspection.transmission_condition && inspection.transmission_condition !== 'PENDING') 
        ? inspection.transmission_condition 
        : (calculatedConditions.transmission || conditionRatings.transmission || "PENDING"),
      exteriorCondition: (inspection.exterior_condition && inspection.exterior_condition !== 'PENDING') 
        ? inspection.exterior_condition 
        : (calculatedConditions.exterior || conditionRatings.exterior || "PENDING"),
      accident: inspection.accident_history || false,
      floodDamage: inspection.flood_damage || false,
      dentsScratches: inspection.dents_scratches || false,
      
      insurance: (() => {
        let insuranceStatus = inspection.insurance_status;
        if (!insuranceStatus || insuranceStatus === 'Unknown') {
          const expiryDate = inspection.insurance_expiry;
          if (expiryDate) {
            const expiry = new Date(expiryDate);
            insuranceStatus = expiry > new Date() ? 'Active' : 'Expired';
          } else {
            insuranceStatus = 'Unknown';
          }
        }
        return {
          status: insuranceStatus,
          insurerName: inspection.insurer_name || "",
          policyNumber: inspection.policy_number || "",
          expiryDate: inspection.insurance_expiry || "",
          policyType: inspection.policy_type || "",
          idvValue: inspection.idv_value || 0
        };
      })(),
      
      tyreDetails: {
        avgLife: inspection.tyre_avg_life || 0,
        tyres: inspection.tyre_details || []
      },
      
      // Use calculated_repairs but deduplicate by part_name to avoid duplicates
      repairs: (() => {
        const seen = new Set();
        return (inspection.calculated_repairs || inspection.repairs || []).filter(r => {
          const key = (r.part_name || '').toLowerCase().trim();
          if (key && seen.has(key)) return false;
          if (key) seen.add(key);
          return true;
        });
      })(),
      calculatedRepairs: []
    },

    rtoVerification: {
      status: inspection.rto_verification_status || "PENDING",
      challans: inspection.challan_data?.challan_details || inspection.challans || [],
      trafficChallans: inspection.challan_data?.total_pending_amount || 0,
      totalChallans: inspection.challan_data?.total_challans || 0,
      pendingCount: inspection.challan_data?.pending_count || 0,
      hypothecation: inspection.hypothecation || null,
      blacklist: inspection.blacklist_status || false,
      financierName: inspection.vaahan_data?.financier || '',
    },

    obdScanningEnabled: inspection.obd_scanning_enabled !== false,
    obdReport: inspection.obd_scanning_enabled === false ? null : (() => {
      // Transform OBD data to include all details for the report
      const obdScan = inspection.obd_scan || {};
      const obdData = obdScan.data || {};
      let rawCodes = obdData.raw_codes || obdData.dtc_codes || inspection.dtc_codes || [];
      const dtcAnalysis = obdData.dtc_analysis;
      
      // If no codes, return basic structure
      if (!rawCodes || rawCodes.length === 0) {
        return {
          connected: inspection.obd_connected || obdScan.completed || false,
          dtcCodes: [],
          liveData: obdData.live_data || inspection.obd_live_data || {},
          systems: []
        };
      }
      
      // Create analysis lookup map
      const analysisMap = {};
      if (dtcAnalysis?.analyzed_codes) {
        dtcAnalysis.analyzed_codes.forEach(c => {
          analysisMap[c.code] = c;
        });
      }
      
      // Component name mapping
      const componentFullNames = {
        'ECM': 'Engine Control Module',
        'TCM': 'Transmission Control Module',
        'ABS': 'Anti-lock Braking System',
        'BCM': 'Body Control Module',
        'EMISSION': 'Emissions System',
        'FUEL': 'Fuel System',
        'UNKNOWN': 'Uncategorized'
      };
      
      const categoryNames = {
        'P': 'Powertrain',
        'U': 'Network/Communication',
        'B': 'Body',
        'C': 'Chassis'
      };
      
      // Group codes by system
      const systemMap = {};
      
      rawCodes.forEach(codeEntry => {
        const code = typeof codeEntry === 'string' ? codeEntry : (codeEntry.code || codeEntry.dtc_code || '');
        const description = typeof codeEntry === 'object' ? (codeEntry.description || codeEntry.meaning || `Diagnostic code ${code}`) : `Diagnostic code ${code}`;
        const analysis = analysisMap[code];
        
        let systemKey = analysis?.component || 'UNKNOWN';
        let systemName = componentFullNames[systemKey] || systemKey;
        
        if (systemKey === 'UNKNOWN' && code) {
          const prefix = code.charAt(0).toUpperCase();
          if (categoryNames[prefix]) {
            systemKey = prefix;
            systemName = categoryNames[prefix];
          }
        }
        
        let severity = 'warning';
        if (analysis?.severity === 'CRITICAL') severity = 'critical';
        else if (analysis?.severity === 'IMPORTANT') severity = 'warning';
        else if (analysis?.severity === 'NON_IMPORTANT') severity = 'info';
        
        const fault = {
          code: code,
          description: description,
          severity: severity,
          aiSeverity: analysis?.severity || null,
          aiReason: analysis?.reason || null,
          status: codeEntry.status || 'Active',
          // Include causes, symptoms, solutions from both analysis and raw code entry
          possibleCauses: analysis?.possible_causes || codeEntry.possible_causes || codeEntry.possibleCauses || [],
          symptoms: analysis?.symptoms || codeEntry.symptoms || [],
          solutions: analysis?.recommended_solutions || analysis?.solutions || codeEntry.recommended_solutions || codeEntry.solutions || []
        };
        
        if (!systemMap[systemKey]) {
          systemMap[systemKey] = {
            name: systemName,
            shortName: systemKey,
            code: systemKey,
            faults: [],
            criticalCount: 0,
            importantCount: 0,
            nonImportantCount: 0
          };
        }
        
        systemMap[systemKey].faults.push(fault);
        if (analysis?.severity === 'CRITICAL') systemMap[systemKey].criticalCount++;
        else if (analysis?.severity === 'IMPORTANT') systemMap[systemKey].importantCount++;
        else systemMap[systemKey].nonImportantCount++;
      });
      
      const systems = Object.values(systemMap)
        .filter(sys => sys.faults.length > 0)
        .sort((a, b) => (b.criticalCount * 100 + b.importantCount) - (a.criticalCount * 100 + a.importantCount))
        .map(sys => ({ ...sys, errorCount: sys.faults.length }));
      
      return {
        connected: inspection.obd_connected || obdScan.completed || true,
        dtcCodes: rawCodes,
        liveData: obdData.live_data || inspection.obd_live_data || {},
        systems: systems,
        totalErrors: rawCodes.length,
        summary: dtcAnalysis?.summary || null
      };
    })(),

    inspectionCategories: inspection.inspection_categories || [],
    categoryRatings: aiInsights.category_ratings || inspection.category_ratings || {},

    footer: {
      companyName: "WiseDrive",
      supportPhone: "+91 88848 54885",
      supportEmail: "support@wisedrive.in",
      website: "www.wisedrive.in"
    },
    
    // Raw AI insights for debugging/display
    aiInsights: aiInsights
  };

  return reportData;
}

function InspectionReportContent({ inspectionId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportStyle, setReportStyle] = useState('standard');
  const [searchParams] = useSearchParams();
  
  // Check for style override in URL params
  const styleOverride = searchParams.get('style');

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    
    // For demo mode, always use sample data
    if (inspectionId === 'demo') {
      setData(inspectionReportData);
      setReportStyle(styleOverride || 'standard');
      setLoading(false);
      return;
    }
    
    // For sample reports with specific styles
    if (inspectionId === 'sample') {
      setData(inspectionReportData);
      setReportStyle(styleOverride || 'standard');
      setLoading(false);
      return;
    }
    
    try {
      // Fetch inspection data from CRM API
      const response = await axios.get(`${API_URL}/api/inspections/${inspectionId}/report`);
      const { inspection, lead, customer } = response.data;
      
      // Get the report style from inspection or fallback
      const inspectionStyle = inspection.report_style || styleOverride || 'standard';
      setReportStyle(inspectionStyle);
      
      // Check if inspection has detailed report data
      // Use CRM data if we have categories OR calculated repairs
      if ((inspection.inspection_categories && inspection.inspection_categories.length > 0) || 
          (inspection.calculated_repairs && inspection.calculated_repairs.length > 0)) {
        // Use CRM data if available
        const reportData = transformInspectionToReport(inspection, lead, customer);
        setData(reportData);
      } else {
        // Use sample data but merge with actual vehicle info
        const mergedData = {
          ...inspectionReportData,
          header: {
            ...inspectionReportData.header,
            customerName: customer?.name || lead?.customer_name || inspectionReportData.header.customerName,
            customerPhone: customer?.phone || lead?.phone_number || inspectionReportData.header.customerPhone,
            vehicleNumber: lead?.vehicle_number || inspection.vehicle_number || inspectionReportData.header.vehicleNumber,
            location: inspection.city || lead?.city || inspectionReportData.header.location,
            inspectedOn: inspection.inspection_date ? new Date(inspection.inspection_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : inspectionReportData.header.inspectedOn,
          },
          vehicleInfo: {
            ...inspectionReportData.vehicleInfo,
            make: lead?.vehicle_make || inspectionReportData.vehicleInfo.make,
            model: lead?.vehicle_model || inspectionReportData.vehicleInfo.model,
            year: lead?.vehicle_year || inspectionReportData.vehicleInfo.year,
            fuel: lead?.fuel_type || inspectionReportData.vehicleInfo.fuel,
            regNo: lead?.vehicle_number || inspectionReportData.vehicleInfo.regNo,
          }
        };
        setData(mergedData);
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      // On any error, use sample data for demonstration
      setData(inspectionReportData);
      setReportStyle(styleOverride || 'standard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (inspectionId) {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionId, styleOverride]);

  if (loading) return <ReportSkeleton />;
  if (error) return <ReportError error={error} onRetry={fetchReport} />;
  if (!data) return null;

  // Render based on report style
  if (reportStyle === 'premium') {
    return (
      <>
        <PremiumReportStyle data={data} />
        <ServiceHistorySection inspectionId={inspectionId} />
      </>
    );
  }
  
  if (reportStyle === 'detailed') {
    return (
      <>
        <DetailedTechnicalReportStyle data={data} />
        <ServiceHistorySection inspectionId={inspectionId} />
      </>
    );
  }
  
  if (reportStyle === 'simple') {
    return (
      <>
        <StandardReportStyle data={data} />
        <ServiceHistorySection inspectionId={inspectionId} />
      </>
    );
  }

  // Default/Standard: Original comprehensive WiseDrive report design
  return (
    <div className="inspection-report min-h-screen">
      {/* Header */}
      <Header data={data.header} />
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto pb-8">
        {/* Hero Section */}
        <HeroSection 
          header={data.header} 
          vehicleInfo={data.vehicleInfo}
          keyInfo={data.keyInfo}
        />
        
        {/* AI Assessment Summary */}
        <AssessmentSummary data={data.assessmentSummary} />
        
        {/* Vehicle Details (Collapsible) */}
        <VehicleDetailsSection data={data.vehicleInfo} />
        
        {/* Key Information with Modals */}
        <KeyInfoSection data={data.keyInfo} inspectionCategories={data.inspectionCategories} />
        
        {/* RTO Verification - right below Key Info */}
        <RTOVerificationSection data={data.rtoVerification} />
        
        {/* Comprehensive Repairs Section */}
        <RepairsSection data={data.keyInfo} />
        
        {/* OBD-2 Report - Hidden when OBD scanning is disabled */}
        {data.obdScanningEnabled && <OBDReportMobile data={data.obdReport} />}
        
        {/* Inspection Details with Horizontal Scroll */}
        <InspectionDetailsSection data={data.inspectionCategories} />
        
        {/* Authorized Service History */}
        <ServiceHistorySection inspectionId={inspectionId} />
      </main>
      
      {/* Footer */}
      <Footer data={data.footer} />
    </div>
  );
}

export default function InspectionReportPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  
  // Get inspection ID from URL params or query string
  const inspectionId = id || searchParams.get('id') || 'demo';

  return (
    <EditModeProvider>
      <InspectionReportContent inspectionId={inspectionId} />
    </EditModeProvider>
  );
}
